class ConditionalFormRules {
    constructor(root = document) {
        this.root = root;
        this.rules = [];
    }

    init() {
        // Query common casings (querySelector is case-sensitive)
        const candidates = Array.from(
            this.root.querySelectorAll(
                '[requiredIf],[requiredif],[hiddenIf],[hiddenif],[showIf],[showif]'
            )
        );

        for (const el of candidates) {
            const requiredExpr = this._getAttr(el, 'requiredIf');
            const hiddenExpr = this._getAttr(el, 'hiddenIf');
            const showExpr = this._getAttr(el, 'showIf');

            if (requiredExpr) this._addRule(el, 'requiredIf', requiredExpr);
            if (hiddenExpr) this._addRule(el, 'hiddenIf', hiddenExpr);
            if (showExpr) this._addRule(el, 'showIf', showExpr);
        }

        this.recalculateAll();
        return this;
    }

    recalculateAll() {
        for (const r of this.rules) this._applyRule(r);
    }

    // -------------------------
    // Internal helpers
    // -------------------------

    _getAttr(el, name) {
        return el.getAttribute(name) ?? el.getAttribute(name.toLowerCase()) ?? null;
    }

    _addRule(el, type, exprRaw) {
        const expr = (exprRaw || '').trim();
        if (!expr) return;

        const leftKey = this._extractLeftOperand(expr);
        const depEl = leftKey ? this._findFieldByKey(leftKey) : null;

        const rule = { el, type, expr, depEl };
        this.rules.push(rule);

        if (depEl) {
            const handler = () => this._applyRule(rule);
            depEl.addEventListener('change', handler);
            depEl.addEventListener('input', handler);

            const form = el.closest('form');
            if (form) {
                form.addEventListener('reset', () =>
                    setTimeout(() => this.recalculateAll(), 0)
                );
            }
        }
    }

    _applyRule(rule) {
        const result = Boolean(this._evaluate(rule.expr));

        if (rule.type === 'requiredIf') {
            this._toggleRequired(rule.el, result);
            return;
        }

        // Unified visibility semantics:
        // - hiddenIf: hide when condition is true
        // - showIf:   hide when condition is false
        const shouldHide =
            rule.type === 'hiddenIf' ? result :
                rule.type === 'showIf'   ? !result :
                    false;

        this._toggleFormRowVisibility(rule.el, shouldHide);
    }

    _toggleFormRowVisibility(el, shouldHide) {
        const row = el.closest('.formrow');
        if (!row) return;

        if (shouldHide) {
            row.style.display = 'none';

            // Disable controls inside row to avoid submit validation; remove required too
            const controls = row.querySelectorAll('input, select, textarea, button');
            controls.forEach(ctrl => {
                if (!ctrl.dataset._cfrPrevDisabled) ctrl.dataset._cfrPrevDisabled = String(ctrl.disabled);
                if (!ctrl.dataset._cfrPrevRequired) ctrl.dataset._cfrPrevRequired = String(ctrl.required);

                ctrl.required = false;
                ctrl.disabled = true;
            });
        } else {
            row.style.display = '';

            const controls = row.querySelectorAll('input, select, textarea, button');
            controls.forEach(ctrl => {
                if (ctrl.dataset._cfrPrevDisabled) {
                    ctrl.disabled = (ctrl.dataset._cfrPrevDisabled === 'true');
                    delete ctrl.dataset._cfrPrevDisabled;
                } else {
                    ctrl.disabled = false;
                }

                if (ctrl.dataset._cfrPrevRequired) {
                    ctrl.required = (ctrl.dataset._cfrPrevRequired === 'true');
                    delete ctrl.dataset._cfrPrevRequired;
                }
            });
        }
    }

    _toggleRequired(el, shouldBeRequired) {
        // If the element is in a hidden row (and thus disabled), keep required off.
        const row = el.closest('.formrow');
        const rowHidden = row && row.style.display === 'none';

        if (rowHidden || el.disabled) {
            el.required = false;
            return;
        }

        el.required = shouldBeRequired;
        if (!shouldBeRequired) el.setCustomValidity?.('');
    }

    _evaluate(expr) {
        const parsed = this._parseExpr(expr);
        if (!parsed) return false;

        const depEl = this._findFieldByKey(parsed.left);
        const leftVal = this._getFieldValue(depEl);

        const left = this._normalizeValue(leftVal);
        const rightRaw = parsed.right;
        const right = this._normalizeValue(rightRaw);

        switch (parsed.op) {
            case '==': return String(left) === String(right);
            case '!=': return String(left) !== String(right);
            case '>':  return Number(left) > Number(right);
            case '>=': return Number(left) >= Number(right);
            case '<':  return Number(left) < Number(right);
            case '<=': return Number(left) <= Number(right);
            case 'contains':
                return String(left ?? '').toLowerCase().includes(String(right ?? '').toLowerCase());
            case 'notContains':
                return !String(left ?? '').toLowerCase().includes(String(right ?? '').toLowerCase());
            case 'in': {
                const list = String(rightRaw)
                    .split(',')
                    .map(s => this._normalizeValue(s.trim()))
                    .filter(Boolean)
                    .map(String);
                return list.includes(String(left));
            }
            default:
                return false;
        }
    }

    _parseExpr(expr) {
        // Order matters
        const ops = ['>=', '<=', '==', '!=', '>', '<', ' contains ', ' notContains ', ' in '];

        for (const opToken of ops) {
            if (opToken.includes(' ')) {
                const idx = expr.indexOf(opToken);
                if (idx > -1) {
                    return {
                        left: expr.slice(0, idx).trim(),
                        op: opToken.trim(),
                        right: expr.slice(idx + opToken.length).trim(),
                    };
                }
            } else {
                const idx = expr.indexOf(opToken);
                if (idx > -1) {
                    return {
                        left: expr.slice(0, idx).trim(),
                        op: opToken,
                        right: expr.slice(idx + opToken.length).trim(),
                    };
                }
            }
        }
        return null;
    }

    _extractLeftOperand(expr) {
        return this._parseExpr(expr)?.left ?? null;
    }

    _findFieldByKey(key) {
        if (!key) return null;

        // 1) exact id (supports ids like company[first_release])
        const byId = this.root.querySelector(`#${CSS.escape(key)}`);
        if (byId) return byId;

        // 2) exact name
        const byName = this.root.querySelector(`[name="${CSS.escape(key)}"]`);
        if (byName) return byName;

        // 3) suffix fallback (kept for robustness)
        const all = Array.from(this.root.querySelectorAll('input, select, textarea'));
        return (
            all.find(x => x.id === key || x.name === key) ||
            all.find(x => (x.id && x.id.endsWith(key)) || (x.name && x.name.endsWith(key))) ||
            null
        );
    }

    _getFieldValue(el) {
        if (!el) return null;

        if (el.type === 'checkbox') return el.checked ? (el.value || '1') : '0';

        if (el.type === 'radio') {
            const group = this.root.querySelectorAll(
                `input[type="radio"][name="${CSS.escape(el.name)}"]`
            );
            const checked = Array.from(group).find(r => r.checked);
            return checked ? checked.value : null;
        }

        if (el.type === 'file') return el.files?.length ? String(el.files.length) : '0';

        if (el.tagName === 'SELECT' && el.multiple) {
            return Array.from(el.selectedOptions).map(o => o.value).join(',');
        }

        return el.value;
    }

    _normalizeValue(v) {
        if (v == null) return '';
        let s = String(v).trim();
        if ((s.startsWith('"') && s.endsWith('"')) || (s.startsWith("'") && s.endsWith("'"))) {
            s = s.slice(1, -1);
        }
        return s;
    }
}

/* --------------------------------------------------
   AUTO INITIALIZE WHEN PAGE LOADS
-------------------------------------------------- */
document.addEventListener('DOMContentLoaded', () => {
    new ConditionalFormRules(document).init();
});